import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navigation, Eye, EyeOff, GripVertical, Save, Loader2, RotateCcw, Plus, Trash2, ExternalLink } from "lucide-react";
import { DynamicIcon } from "../components/shared/IconMap";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const SYSTEM_NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: "LayoutDashboard", group: "Core", removable: false },
  { path: "/blueprints", label: "Blueprints", icon: "FileText", group: "Core" },
  { path: "/machine-index", label: "Machine Index", icon: "BookOpen", group: "Core" },
  { path: "/work-orders", label: "Work Orders", icon: "ClipboardList", group: "Core" },
  { path: "/qr-scanner", label: "QR Scanner", icon: "ScanLine", group: "Core" },
  { path: "/maintenance", label: "Maintenance", icon: "Calendar", group: "Operations" },
  { path: "/maintenance/calendar", label: "Maint. Calendar", icon: "CalendarDays", group: "Operations" },
  { path: "/network-scan", label: "Network Scan", icon: "Search", group: "Operations" },
  { path: "/parts", label: "Parts Catalog", icon: "Package", group: "Operations" },
  { path: "/compliance", label: "Compliance", icon: "ShieldCheck", group: "Compliance" },
  { path: "/audit-trail", label: "Audit Trail", icon: "Shield", group: "Compliance" },
  { path: "/users", label: "Users", icon: "Users", group: "Admin" },
  { path: "/machine-settings", label: "Machine Settings", icon: "Settings", group: "Admin" },
  { path: "/branding", label: "Branding", icon: "Paintbrush", group: "Admin" },
  { path: "/customization", label: "Customization", icon: "Wrench", group: "Admin" },
  { path: "/schedulers", label: "Schedulers", icon: "Clock", group: "Admin" },
  { path: "/connectors", label: "Connectors", icon: "Plug", group: "Integrations" },
  { path: "/m365", label: "Microsoft 365", icon: "LayoutGrid", group: "Integrations" },
  { path: "/sap-integration", label: "SAP S/4HANA", icon: "Database", group: "Integrations" },
  { path: "/ldap-config", label: "AD / LDAP", icon: "Server", group: "Integrations" },
  { path: "/qms", label: "QMS", icon: "ShieldCheck", group: "Integrations" },
  { path: "/aveva-pi", label: "AVEVA PI", icon: "Activity", group: "Integrations" },
  { path: "/opcua", label: "OPC UA Gateway", icon: "Gauge", group: "Integrations" },
  { path: "/forge-viewer", label: "DWG Viewer", icon: "Box", group: "Integrations" },
  { path: "/about", label: "About", icon: "BookOpen", group: "System" },
];

const GROUPS = ["Core", "Operations", "Compliance", "Admin", "Integrations", "System", "Custom"];

export default function NavBuilderPage({ onLogout, userRole, isFeatureEnabled }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [customLinks, setCustomLinks] = useState([]);

  const fetchNav = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/customization/navigation`, { headers: getAuthHeaders() });
      if (resp.data.is_custom && resp.data.groups?.length > 0) {
        // Load saved configuration
        const allItems = [];
        resp.data.groups.forEach(group => {
          (group.items || []).forEach(item => {
            allItems.push({ ...item, group: group.label, visible: item.visible !== false });
          });
        });
        if (allItems.length > 0) {
          setItems(allItems);
          setCustomLinks(allItems.filter(i => i.is_custom));
          setLoading(false);
          return;
        }
      }
      // Default: use system nav items
      setItems(SYSTEM_NAV_ITEMS.map(i => ({ ...i, visible: true })));
    } catch { setItems(SYSTEM_NAV_ITEMS.map(i => ({ ...i, visible: true }))); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchNav(); }, [fetchNav]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const groupMap = {};
      items.forEach(item => {
        const g = item.group || "Core";
        if (!groupMap[g]) groupMap[g] = { label: g, items: [] };
        groupMap[g].items.push({ path: item.path, label: item.label, icon: item.icon, visible: item.visible, is_custom: item.is_custom, external: item.external });
      });
      await axios.put(`${API}/customization/navigation`, { groups: Object.values(groupMap) }, { headers: getAuthHeaders() });
      toast.success("Navigation saved");
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset navigation to system defaults?")) return;
    setItems(SYSTEM_NAV_ITEMS.map(i => ({ ...i, visible: true })));
    setCustomLinks([]);
    setDirty(true);
  };

  const toggleVisible = (i) => {
    setItems(prev => prev.map((item, j) => j === i ? { ...item, visible: !item.visible } : item));
    setDirty(true);
  };

  const updateItem = (i, field, val) => {
    setItems(prev => prev.map((item, j) => j === i ? { ...item, [field]: val } : item));
    setDirty(true);
  };

  const moveItem = (i, dir) => {
    const arr = [...items];
    const ni = i + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[i], arr[ni]] = [arr[ni], arr[i]];
    setItems(arr);
    setDirty(true);
  };

  const addCustomLink = () => {
    const newItem = { path: "", label: "Custom Link", icon: "ExternalLink", group: "Custom", visible: true, is_custom: true, external: true };
    setItems(prev => [...prev, newItem]);
    setDirty(true);
  };

  const removeItem = (i) => {
    if (!items[i]?.is_custom && items[i]?.removable !== true) {
      // System items: just hide instead of remove
      toggleVisible(i);
      return;
    }
    setItems(prev => prev.filter((_, j) => j !== i));
    setDirty(true);
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" size={24} /></div></Layout>;

  const groupedItems = {};
  items.forEach((item, i) => {
    const g = item.group || "Core";
    if (!groupedItems[g]) groupedItems[g] = [];
    groupedItems[g].push({ ...item, _index: i });
  });

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="nav-builder-page">
        <div className="flex items-center justify-between">
          <PageHeader title="Navigation Builder" subtitle="Show, hide, reorder, and group sidebar navigation items" testId="nav-builder-title" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset} className="rounded-none text-xs" data-testid="nav-reset-btn"><RotateCcw size={12} className="mr-1" /> Reset</Button>
            <Button onClick={addCustomLink} className="rounded-none bg-[#D97706] text-white text-xs" data-testid="nav-add-link-btn"><Plus size={12} className="mr-1" /> Custom Link</Button>
            <Button onClick={handleSave} disabled={saving || !dirty} className="rounded-none bg-[#2563EB] text-white" data-testid="nav-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />} Save
            </Button>
          </div>
        </div>

        {GROUPS.filter(g => groupedItems[g]?.length > 0).map(group => (
          <div key={group} className="bg-white border border-[#E2E8F0]" data-testid={`nav-group-${group}`}>
            <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#FAFAFA]">
              <span className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">{group}</span>
              <span className="text-xs text-[#94A3B8] ml-2">{groupedItems[group].filter(i => i.visible).length}/{groupedItems[group].length} visible</span>
            </div>
            <div className="divide-y divide-[#E2E8F0]">
              {groupedItems[group].map((item) => (
                <div key={item._index} className={`px-4 py-2.5 flex items-center gap-3 ${!item.visible ? "opacity-40" : ""}`} data-testid={`nav-item-${item._index}`}>
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveItem(item._index, -1)} className="text-[#94A3B8] hover:text-[#0F172A] text-xs">▲</button>
                    <button onClick={() => moveItem(item._index, 1)} className="text-[#94A3B8] hover:text-[#0F172A] text-xs">▼</button>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center text-[#64748B]">
                    <DynamicIcon name={item.icon || "FileText"} size={16} />
                  </div>
                  {item.is_custom ? (
                    <>
                      <Input value={item.label} onChange={e => updateItem(item._index, "label", e.target.value)} className="w-36 rounded-none text-xs h-7" />
                      <Input value={item.path} onChange={e => updateItem(item._index, "path", e.target.value)} className="flex-1 rounded-none font-mono text-xs h-7" placeholder="https://..." />
                      <select value={item.icon || "ExternalLink"} onChange={e => updateItem(item._index, "icon", e.target.value)} className="border border-[#E2E8F0] text-xs px-1 py-0.5 w-28">
                        {["ExternalLink", "Link", "Globe", "FileText", "BookOpen", "Shield", "Activity", "Gauge", "Database"].map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-[#0F172A] w-40">{item.label}</span>
                      <span className="text-[9px] font-mono text-[#94A3B8] flex-1">{item.path}</span>
                    </>
                  )}
                  <button onClick={() => toggleVisible(item._index)} className={item.visible ? "text-[#10B981]" : "text-[#CBD5E1]"}>
                    {item.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  {item.is_custom && (
                    <button onClick={() => removeItem(item._index)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-[#F0F9FF] border border-[#BAE6FD] p-4">
          <p className="text-xs text-[#0369A1]"><strong>Tip:</strong> Use the eye icon to show/hide pages. System pages cannot be deleted but can be hidden. Add custom external links for company intranet, SharePoint sites, or other tools.</p>
        </div>
      </div>
    </Layout>
  );
}
